import { SortOrderEnum } from '@/lib/types';
import { gql } from '@apollo/client';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import { FilterInput } from '@/components/publicSearch/constants';
import type { FilterGroup } from '@/components/publicSearch/constants';

export type SavedFilterSortItemDto = {
  field: string;
  sort?: string | null;
};

export type SavedFilterGroupDto = {
  logicalOperator: string;
  filters: FilterInput[];
};

export type SavedFilterDto = {
  id: string;
  searchType: string;
  name: string;
  searchTerm: string;
  filters: FilterInput[];
  saleDateFilter?: string | null;
  saleTypeFilterValues: string[];
  gridSortModel: SavedFilterSortItemDto[];
  isAiMode: boolean;
  filterGroups: SavedFilterGroupDto[];
  groupsOperator: string;
  aiSearchPrompt: string;
  createdAt: string;
  modifiedAt: string;
};

export type GetSavedFiltersResponse = {
  savedFilters: SavedFilterDto[];
};

export type GetSavedFiltersVariables = {
  searchType: string;
};

type SavedFilterInput = {
  searchType: string;
  name: string;
  searchTerm: string;
  filters: FilterInput[];
  saleDateFilter?: string | null;
  saleTypeFilterValues?: string[];
  gridSortModel: SavedFilterSortItemDto[];
  isAiMode: boolean;
  filterGroups: SavedFilterGroupDto[];
  groupsOperator: string;
  aiSearchPrompt: string;
};

export type CreateSavedFilterResponse = {
  createSavedFilter: SavedFilterDto;
};

export type CreateSavedFilterVariables = {
  input: SavedFilterInput;
};

export type UpdateSavedFilterResponse = {
  updateSavedFilter: SavedFilterDto;
};

export type UpdateSavedFilterVariables = {
  id: string;
  input: SavedFilterInput;
};

export type DeleteSavedFilterResponse = {
  deleteSavedFilter: boolean;
};

export type DeleteSavedFilterVariables = {
  id: string;
};

const savedFilterFields = `
  id
  searchType
  name
  searchTerm
  filters {
    field
    operator
    value
  }
  saleDateFilter
  saleTypeFilterValues
  gridSortModel {
    field
    sort
  }
  isAiMode
  filterGroups {
    logicalOperator
    filters {
      field
      operator
      value
    }
  }
  groupsOperator
  aiSearchPrompt
  createdAt
  modifiedAt
`;

export const GET_SAVED_FILTERS = gql`
  query GetSavedFilters($searchType: String!) {
    savedFilters(searchType: $searchType) {
      ${savedFilterFields}
    }
  }
`;

export const CREATE_SAVED_FILTER = gql`
  mutation CreateSavedFilter($input: SavedFilterInput!) {
    createSavedFilter(input: $input) {
      ${savedFilterFields}
    }
  }
`;

export const UPDATE_SAVED_FILTER = gql`
  mutation UpdateSavedFilter($id: String!, $input: SavedFilterInput!) {
    updateSavedFilter(id: $id, input: $input) {
      ${savedFilterFields}
    }
  }
`;

export const DELETE_SAVED_FILTER = gql`
  mutation DeleteSavedFilter($id: String!) {
    deleteSavedFilter(id: $id)
  }
`;

/** Map MUI GridSortModel to API input. */
export function mapGridSortToDto(
  model: GridSortModel | undefined | null,
): SavedFilterSortItemDto[] {
  if (!model?.length) return [];
  return model.map((s) => ({ field: s.field, sort: s.sort ?? null }));
}

/** Map API DTO sort items to MUI GridSortModel. */
export function mapDtoToGridSortModel(
  items: SavedFilterSortItemDto[] | undefined | null,
): GridSortModel {
  if (!items?.length) return [];
  return items.map((s) => {
    let sort: SortOrderEnum | null;
    if (s.sort === SortOrderEnum.ASC || s.sort === SortOrderEnum.DESC) {
      sort = s.sort;
    } else if (s.sort) {
      sort = s.sort as SortOrderEnum;
    } else {
      sort = null;
    }
    return { field: s.field, sort };
  });
}

/** Map FilterGroup[] to GraphQL saved-filter group input shape. */
export function mapFilterGroupsToDto(
  groups: FilterGroup[],
): SavedFilterGroupDto[] {
  return groups.map((g) => ({
    logicalOperator: g.logicalOperator,
    filters: g.filters.map((f) => ({
      field: f.field,
      operator: f.operator,
      value: f.value,
    })),
  }));
}
